import { linesOwingMoney } from "@/lib/appointment/abonements";
import { AppointmentRecord } from "@/lib/types/appointment";
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";
import { ensureUtcIso, toUtcIsoFromSalon } from "@/lib/time";
// Centralized cost calculation engine
import {
  calculateTotalAmount,
  calculateServiceCostFields,
  getServiceUnitPrice,
  validateServiceCostFields,
  type ValidationStage,
} from "@/lib/appointment/costEngine";


/**
 * Prepare an AppointmentRecord for sending to the API by ensuring
 * datetime fields are true UTC instants (ISO with trailing "Z", no ms).
 *
 * - If appointment.datetime exists: normalize via ensureUtcIso().
 * - Otherwise, if appointment.date + startTime exist: build datetime via toUtcIsoFromSalon().
 * - If endTime exists: build end_datetime similarly and attach to the payload as `end_datetime`
 *   (backend may expect `length` / `end_datetime` or calculate based on `length` — adjust as needed).
 *
 * This function returns a shallow-cloned object safe to send to the server.
 * It intentionally does not deep-convert every nested field (visit_history, consumables, etc.)
 * — extend as necessary for your API contract.
 */
export function normalizeAppointmentForApi(appt: AppointmentRecord): AppointmentRecord {
  if (!appt) return appt;

  const tz = getCompanyRegistry().getTimezone() || "UTC";

  // shallow clone
  const out: AppointmentRecord = { ...appt };

  try {
    if (out.datetime && typeof out.datetime === "string") {
      // incoming datetime might be offset-less or already have Z/offset.
      out.datetime = ensureUtcIso(out.datetime, tz);
    } else if (out.date && out.startTime) {
      // build from salon-local date + time
      out.datetime = toUtcIsoFromSalon(out.date, out.startTime, tz);
    }

    // Derive end_datetime when endTime is present (useful for some backends)
    if (out.date && out.endTime) {
      try {
        (out as any).end_datetime = toUtcIsoFromSalon(out.date, out.endTime, tz);
      } catch (err) {
        // ignore derivation errors — backend can fallback to length/duration
      }
    }

    // created_datetime is commonly produced on server; if client sets it, normalize too
    if ((out as any).created_datetime && typeof (out as any).created_datetime === "string") {
      (out as any).created_datetime = ensureUtcIso((out as any).created_datetime, tz);
    }
  } catch (e) {
    // If normalization fails, rethrow to make the issue visible in dev
    throw e;
  }

  // Ensure soft-delete flag is present as 0|1 for the backend.
  // (If omitted by caller, default to 0.)
  (out as any).deleted = Number(appt?.deleted ?? 0) as 0 | 1;

  // The backend resolves the client card by the TOP-LEVEL client_id (admin-record/save,
  // appointment_service). Picking a phone suggestion set only client.id, so the card was
  // looked up by phone again — and a client whose stored number fails today's phone
  // check could not be booked at all (clients README §5.2 #16).
  const pickedClientId = (out as any).client?.id;
  if (pickedClientId && !(out as any).client_id) (out as any).client_id = pickedClientId;

  /* --- Services normalization using centralized costEngine --- */

  if (Array.isArray((out as any).services)) {
    (out as any).services = (out as any).services.map((s: any) => {
      // Use centralized calculateServiceCostFields to compute all cost fields
      // Detect if this is truly a new appointment by checking if id is absent or falsy
      const isCreation = !(out as any).id;
      const costFields = calculateServiceCostFields({
        cost: Number(s.cost) || getServiceUnitPrice(s),
        amount: s.amount ?? s.quantity ?? 1,
        discount: s.discount ?? 0,
        manualCost: s.manual_cost,
        firstCost: s.first_cost,
        isCreation,
      });

      return {
        ...s,
        // Spread all calculated cost fields from the centralized engine
        ...costFields,
        // Also set quantity alias if it was present
        ...(Object.prototype.hasOwnProperty.call(s, "quantity") ? { quantity: costFields.amount } : {}),
      };
    });
  }

  /* -------------------------- payment method selection --------------------------- */

  // src/lib/services/appointmentPayload.ts

  /* -------------------------- payment method selection --------------------------- */

  if (out.paymentSection && Array.isArray(out.paymentSection.paymentMethods)) {
    const { paymentMode: _paymentMode, ...restSection } = out.paymentSection as any;
    out.paymentSection = restSection as any;

    // Use centralized calculateTotalAmount instead of local computeTotalDue
    // lines a client package pays owe no money (abonements.ts)
    const totalDue = calculateTotalAmount(
      linesOwingMoney((out as any).services || [], (out.paymentSection as any)?.abonementUses)
    );
    const methods = out.paymentSection.paymentMethods;
    const normalizedMethods = methods.map((m: any) => {
      if (!m?.account) return m;
      const paid = Number(m.account.paid_amount);
      return {
        ...m,
        account: {
          ...m.account,
          paid_amount: Number.isFinite(paid) && paid > 0 ? paid : 0,
        },
      };
    });

    // Calculate what the "derived" status would be based on methods alone
    const paidTotal = normalizedMethods.reduce(
      (sum: number, m: any) => sum + (Number(m?.account?.paid_amount) || 0),
      0
    );
    const matchesTotal = Math.abs(paidTotal - totalDue) < 0.01;
    const calculatedPaidFull = paidTotal > 0 ? (matchesTotal ? 1 : 2) : 0;

    // FIX: Trust the existing `paid_full` flag from the UI context if it exists.
    // Only fall back to the calculated value if the flag is missing.
    // This prevents overwriting "paid_full=1" with "0" just because paymentMethods might be empty/unloaded.
    const existingPaidFull = (out as any).paid_full ?? (out as any).paymentSection?.paidFull;
    const finalPaidFull = typeof existingPaidFull === 'number' ? existingPaidFull : calculatedPaidFull;
    const finalIsPaid = finalPaidFull > 0;

    out.paymentSection = {
      ...out.paymentSection,
      isPaid: finalIsPaid,
      paidFull: finalPaidFull,
      paymentMethods: normalizedMethods,
    };
    (out as any).paid_full = finalPaidFull;
    (out as any).is_paid = finalIsPaid;
  }

  /* --- Validate services before returning (per docs/task.md) --- */
  if (Array.isArray((out as any).services) && (out as any).services.length > 0) {
    // Detect stage by checking if id exists (existing appointment = processing)
    const stage: ValidationStage = (out as any).id ? 'processing' : 'creation';
    const validationErrors: string[] = [];

    (out as any).services.forEach((s: any, idx: number) => {
      const result = validateServiceCostFields(s, stage);
      if (!result.valid) {
        validationErrors.push(`Service ${idx + 1} (${s.title || 'Untitled'}): ${result.errors.join(', ')}`);
      }
    });

    if (validationErrors.length > 0) {
      console.error('[appointmentPayload] Validation failed:', validationErrors);
      // Log but don't throw for now - backend will catch and return proper error
      // throw new Error(`Appointment validation failed: ${validationErrors.join('; ')}`);
    }
  }

  return out;
}

export default { normalizeAppointmentForApi };
